import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SceneState } from './sceneSlice'
import { FireflyFulfillableItemCreditModel } from '@store/middleware/studio/fireflySaga'
import { LocaleString } from '@concerns/i18n/types'

export enum ContentClass {
  AUTO = 'auto',
  PHOTO = 'photo',
  ART = 'art',
  VECTOR = 'vector'
}

export enum AspectRatio {
  LANDSCAPE = 'LANDSCAPE',
  PORTRAIT = 'PORTRAIT',
  SQUARE = 'SQUARE',
  WIDESCREEN = 'WIDESCREEN'
}

export enum PresetCategory {
  ALL = 'all',
  POPULAR = 'popular',
  MOVEMENTS = 'movements',
  THEMES = 'themes',
  TECHNIQUES = 'techniques',
  EFFECTS = 'effects',
  MATERIALS = 'materials',
  CONCEPTS = 'concepts',
  COLOR_AND_TONE = 'colors',
  LIGHTING = 'lighting',
  CAMERA_ANGLE = 'composition'
}

export type StyleImageReference = {
  type: 'preset' | 'user-upload' | 'generated-output'
  url: string
}

export type GeneratedImage = {
  url?: string
  selected: boolean
}

type DownloadURL = {
  originalURL: string
  downloadURL: string
}

type AccessProfileWorkflowResult = {
  locale: LocaleString
  response: string
}

type Paywall = 'cta' | 'none' | 'quota'

type FireflyCreditPlan = {
  fulfillableItemCreditModel: FireflyFulfillableItemCreditModel
  canUpgrade: boolean
}

export interface FireflyState {
  aspectRatio: AspectRatio
  contentClass: ContentClass
  styleStrength: number
  guideStrength: number
  structureStrength: number
  visualIntensity: number
  generatedImages: GeneratedImage[]
  imageGenerationStatus:
    | 'idle'
    | 'generating-similar-images'
    | 'generating-new-images'
  prompt: string
  stylePresets: string[]
  selectedPresetCategory: PresetCategory
  visibleToast: 'none' | 'completed' | 'error' | 'warning'
  styleImageReference: StyleImageReference | null
  previousSceneFramePropertiesOnShow: {
    frameEnabled: SceneState['frameEnabled']
    frameSize: SceneState['frameSize']
    frameType: SceneState['frameType']
  } | null
  imgReference: 'color' | 'depth'
  sceneColorsAdherence: number
  showOnboardingDialog: boolean
  showLimitedFreeTimeToast: boolean
  showUploadingImageRightsDialog: boolean
  hasViewedUploadingImageRightsDialog: boolean
  showToSPopup: boolean
  showEmptySceneDialog: boolean
  downloadURLs: DownloadURL[]
  showFreeUserDialog: boolean
  showPaywallToast: boolean
  showPaywall: Paywall | null
  orderStatus: string | null
  accessProfileWorkflowResult: AccessProfileWorkflowResult | null
  paywallURL: string
  showCreditWarningToast: boolean
  fireflyCreditPlan: FireflyCreditPlan
}

type KeyValue<
  T extends { [key: string]: any },
  U extends keyof T
> = U extends any ? { key: U; value: T[U] } : never

const initialState: FireflyState = {
  aspectRatio: AspectRatio.LANDSCAPE,
  contentClass: ContentClass.AUTO,
  structureStrength: 100,
  styleStrength: 50,
  guideStrength: 0,
  visualIntensity: 6,
  generatedImages: [
    { selected: false },
    { selected: false },
    { selected: false },
    { selected: false }
  ],
  downloadURLs: [],
  imageGenerationStatus: 'idle',
  prompt: '',
  stylePresets: [],
  selectedPresetCategory: PresetCategory.POPULAR,
  visibleToast: 'none',
  styleImageReference: null,
  previousSceneFramePropertiesOnShow: null,
  imgReference: 'depth',
  sceneColorsAdherence: 50,
  showOnboardingDialog: false,
  showLimitedFreeTimeToast: false,
  showUploadingImageRightsDialog: false,
  hasViewedUploadingImageRightsDialog: false,
  showToSPopup: false,
  showEmptySceneDialog: false,
  showFreeUserDialog: false,
  showPaywallToast: false,
  showPaywall: 'none',
  orderStatus: null,
  accessProfileWorkflowResult: null,
  paywallURL: '',
  showCreditWarningToast: false,
  fireflyCreditPlan: {
    fulfillableItemCreditModel: 'FREE-EXHAUSTABLE',
    canUpgrade: true
  }
}

export type PropertyPayload = KeyValue<FireflyState, keyof typeof initialState>

export const firefly = createSlice({
  name: 'firefly',
  initialState,
  reducers: {
    setPropertyState: (
      state,
      { payload }: PayloadAction<Partial<PropertyPayload>>
    ) => {
      state[payload.key] = payload.value
    },
    addStylePreset: (state, { payload }: PayloadAction<string>) => {
      state.stylePresets = [...state.stylePresets, payload]
    },
    removeStylePreset: (state, { payload }: PayloadAction<string>) => {
      state.stylePresets = state.stylePresets.filter(
        preset => preset !== payload
      )
    },
    clearStylePresets: state => {
      state.stylePresets = []
    },
    generateImages: () => {},
    generateSimilarImages: (
      _,
      { payload }: PayloadAction<{ imgUrl: string }>
    ) => {},
    stopImageGeneration: () => {},
    setGeneratedImages: (
      state,
      { payload }: PayloadAction<FireflyState['generatedImages']>
    ) => {
      state.generatedImages = payload
    },
    setDownloadURLs: (
      state,
      { payload }: PayloadAction<FireflyState['downloadURLs']>
    ) => {
      state.downloadURLs = payload
    },
    addDownloadURL: (_, { payload }: PayloadAction<DownloadURL>) => {},
    setImageGenerationStatus: (
      state,
      { payload }: PayloadAction<FireflyState['imageGenerationStatus']>
    ) => {
      state.imageGenerationStatus = payload
    },
    setPrompt: (state, { payload }: PayloadAction<FireflyState['prompt']>) => {
      state.prompt = payload
    },
    selectGeneratedImage: (
      _,
      { payload }: PayloadAction<{ url: string }>
    ) => {},
    setSelectedPresetCategory: (
      state,
      { payload }: PayloadAction<FireflyState['selectedPresetCategory']>
    ) => {
      state.selectedPresetCategory = payload
    },
    resetState: state => {
      state.aspectRatio = AspectRatio.LANDSCAPE
      state.contentClass = ContentClass.PHOTO
      state.generatedImages = [
        { selected: false },
        { selected: false },
        { selected: false },
        { selected: false }
      ]
      state.prompt = ''
      state.imageGenerationStatus = 'idle'
      state.stylePresets = []
      state.selectedPresetCategory = PresetCategory.POPULAR
      state.visibleToast = 'none'
      state.styleImageReference = null
      state.previousSceneFramePropertiesOnShow = null
    },
    setVisibleToast: (
      state,
      { payload }: PayloadAction<FireflyState['visibleToast']>
    ) => {
      state.visibleToast = payload
    },
    setShowOnboardingDialog: (
      state,
      { payload }: PayloadAction<FireflyState['showOnboardingDialog']>
    ) => {
      state.showOnboardingDialog = payload
    },
    setShowLimitedFreeTimeToast: (
      state,
      { payload }: PayloadAction<FireflyState['showLimitedFreeTimeToast']>
    ) => {
      console.log('form slice setShowLimitedFreeTimeToast', payload)
      state.showLimitedFreeTimeToast = payload
    },
    setShowUploadingImageRightsDialog: (
      state,
      { payload }: PayloadAction<FireflyState['showUploadingImageRightsDialog']>
    ) => {
      state.showUploadingImageRightsDialog = payload
    },
    setHasViewedUploadingImageRightsDialog: (
      state,
      {
        payload
      }: PayloadAction<FireflyState['hasViewedUploadingImageRightsDialog']>
    ) => {
      state.hasViewedUploadingImageRightsDialog = payload
    },
    setShowToSPopup: (
      state,
      { payload }: PayloadAction<FireflyState['showToSPopup']>
    ) => {
      state.showToSPopup = payload
    },
    setShowEmptySceneDialog: (
      state,
      { payload }: PayloadAction<FireflyState['showEmptySceneDialog']>
    ) => {
      state.showEmptySceneDialog = payload
    },
    setShowFreeUserDialog: (
      state,
      { payload }: PayloadAction<FireflyState['showFreeUserDialog']>
    ) => {
      state.showFreeUserDialog = payload
    },
    setShowPaywallToast: (
      state,
      { payload }: PayloadAction<FireflyState['showPaywallToast']>
    ) => {
      state.showPaywallToast = payload
    },
    setShowPaywall: (
      state,
      { payload }: PayloadAction<FireflyState['showPaywall']>
    ) => {
      state.showPaywall = payload
    },
    setOrderStatus: (
      state,
      { payload }: PayloadAction<FireflyState['orderStatus']>
    ) => {
      state.orderStatus = payload
    },
    setAccessProfileWorkflowResult: (
      state,
      { payload }: PayloadAction<FireflyState['accessProfileWorkflowResult']>
    ) => {
      state.accessProfileWorkflowResult = payload
    },
    setPaywallURL: (
      state,
      { payload }: PayloadAction<FireflyState['paywallURL']>
    ) => {
      state.paywallURL = payload
    },
    setShowCreditWarningToast: (
      state,
      { payload }: PayloadAction<FireflyState['showCreditWarningToast']>
    ) => {
      state.showCreditWarningToast = payload
    },
    loadFireflyCreditPlan: () => {},
    setFireflyCreditPlan: (
      state,
      { payload }: PayloadAction<FireflyState['fireflyCreditPlan']>
    ) => {
      state.fireflyCreditPlan = payload
    }
  }
})

export const {
  clearStylePresets,
  generateImages,
  generateSimilarImages,
  stopImageGeneration,
  resetState,
  setGeneratedImages,
  setDownloadURLs,
  addDownloadURL,
  setImageGenerationStatus,
  setPrompt,
  setPropertyState,
  selectGeneratedImage,
  setSelectedPresetCategory,
  setVisibleToast,
  setShowOnboardingDialog,
  setShowLimitedFreeTimeToast,
  setShowUploadingImageRightsDialog,
  setHasViewedUploadingImageRightsDialog,
  setShowToSPopup,
  setShowEmptySceneDialog,
  setShowFreeUserDialog,
  setShowPaywallToast,
  setShowPaywall,
  setOrderStatus,
  setAccessProfileWorkflowResult,
  setPaywallURL,
  setShowCreditWarningToast,
  loadFireflyCreditPlan,
  setFireflyCreditPlan
} = firefly.actions

export default firefly.reducer
